import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Flame, Magnet, Gauge, Droplets, Waves, ArrowRight, Stethoscope } from "lucide-react";
import Reveal, { EASE } from "@/components/Reveal";
import { SYMPTOMS } from "@/constants/content";

const ICONS = { flame: Flame, magnet: Magnet, gauge: Gauge, droplets: Droplets, waves: Waves };

const fmt = (n) => `$${n.toFixed(2)}`;

export default function Diagnostic() {
  const [active, setActive] = useState(null);
  const s = SYMPTOMS.find((x) => x.id === active);

  return (
    <section id="diagnostic" className="relative py-20 sm:py-28" data-testid="diagnostic-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <p className="eyebrow text-brandsky-deep">Quick diagnosis</p>
          <h2 className="font-display mt-3 max-w-2xl text-3xl font-extrabold tracking-tight text-ink sm:text-4xl lg:text-[2.75rem] lg:leading-[1.05]">
            Something feels off chairside? Start here.
          </h2>
          <p className="mt-4 max-w-xl text-[15.5px] leading-relaxed text-slate-600">
            Pick the symptom — we'll tell you the likely cause and the fix, before you even pick up the phone.
          </p>
        </Reveal>

        <div className="mt-12 grid gap-5 lg:grid-cols-12">
          <Reveal className="lg:col-span-6">
            <div className="grid gap-3 sm:grid-cols-2">
              {SYMPTOMS.map((sym, i) => {
                const Icon = ICONS[sym.icon];
                const isActive = active === sym.id;
                return (
                  <motion.button
                    key={sym.id}
                    onClick={() => setActive(sym.id)}
                    data-testid={`diagnostic-symptom-${sym.id}`}
                    whileTap={{ scale: 0.97 }}
                    className={`flex items-center gap-4 rounded-2xl border p-5 text-left transition-all duration-300 ${
                      isActive
                        ? "border-ink bg-ink text-white shadow-[0_16px_40px_rgba(22,50,79,0.3)]"
                        : "border-ink/10 bg-white text-ink hover:border-brandsky hover:shadow-[0_10px_30px_rgba(59,191,230,0.18)]"
                    } ${i === SYMPTOMS.length - 1 ? "sm:col-span-2" : ""}`}
                  >
                    <span
                      className={`rounded-xl p-2.5 transition-colors ${
                        isActive ? "bg-white/15 text-brandsky" : "bg-brandsky-soft text-brandsky-deep"
                      }`}
                    >
                      <Icon className="h-5 w-5" />
                    </span>
                    <span className="text-[15px] font-bold">{sym.label}</span>
                  </motion.button>
                );
              })}
            </div>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-6">
            <div
              className="relative h-full min-h-[320px] overflow-hidden rounded-[22px] border border-ink/10 bg-white p-7 shadow-[0_10px_40px_rgba(22,50,79,0.08)] sm:p-9"
              data-testid="diagnostic-result"
            >
              <AnimatePresence mode="wait">
                {s ? (
                  <motion.div
                    key={s.id}
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -12 }}
                    transition={{ duration: 0.4, ease: EASE }}
                  >
                    <p className="eyebrow text-brandleaf-deep">Likely cause</p>
                    <p className="font-display mt-2 text-xl font-bold leading-snug text-ink">{s.cause}</p>

                    <div className="mt-6 rounded-2xl border border-brandsky/30 bg-brandsky-soft p-5">
                      <p className="eyebrow text-brandsky-deep">Recommended fix</p>
                      <div className="mt-2 flex items-center justify-between gap-4">
                        <p className="text-[15px] font-bold text-ink">{s.fix}</p>
                        <p className="font-mono text-lg font-bold text-ink" data-testid="diagnostic-price">
                          {s.call ? "Please call" : s.price ? fmt(s.price) : `from ${fmt(s.priceFrom)}`}
                        </p>
                      </div>
                      <p className="mt-2 text-[13.5px] leading-relaxed text-slate-600">{s.note}</p>
                    </div>

                    <div className="mt-7 flex flex-wrap gap-3">
                      <a
                        href="#intake"
                        data-testid="diagnostic-book-button"
                        className="btn-lift inline-flex items-center gap-2 rounded-full bg-ink px-6 py-3 text-[13.5px] font-bold text-white hover:bg-abyss"
                      >
                        Book this fix <span aria-hidden>→</span>
                      </a>
                      <a
                        href="tel:+17162566630"
                        className="inline-flex items-center gap-2 rounded-full border border-ink/15 px-6 py-3 text-[13.5px] font-bold text-ink hover:border-ink/40"
                        data-testid="diagnostic-call-button"
                      >
                        Or call for a free estimate
                      </a>
                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    key="empty"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="flex h-full min-h-[280px] flex-col items-start justify-center"
                  >
                    <span className="rounded-2xl bg-cloud p-4">
                      <Stethoscope className="h-7 w-7 text-brandsky-deep" />
                    </span>
                    <p className="font-display mt-5 text-xl font-bold text-ink">Select a symptom on the left</p>
                    <p className="mt-2 max-w-sm text-[14px] leading-relaxed text-slate-500">
                      Not on the list? Call or text (716) 256-6630 — describing what the handpiece is doing is half
                      the diagnosis.
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}